import { DEFAULT_SELECT_PAGE_SIZE, FETCH_QUERIES_NAME } from '@common/constants';
import { ComponentType, IItemResponse, ToastMessage, toastMessage, useForm } from '@gcl/react-core';
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { ERolePermission, ETwcTwcmApplicationPermission } from '@userProfile/permission/permission.constants';
import { useMutationWithPageTopBarLoadingButton } from '@common/useMutationWithPageTopBarLoadingButton.hooks';
import { AxiosErrorResponse } from '@core/error.models';
import { routes } from '@routes/routes.models';
import { useNavigate } from '@routes/routes.hooks';
import { AppConfigContext } from '@config/AppConfig';
import { IProjectUserProfileOption } from '@userProfile/projectUserProfile.models';
import { isPermittedAsSiteOperationUser, isPermittedAsSystemUser } from '@userProfile/permission/permission.services';
import { useFetchProjectUserProfileOptionsWithSearch } from '@userProfile/projectUserProfile.queries';
import { useProjectIsArchived } from '@config/config.hooks';
import { ProjectTwcGeDetailFormKeys } from './projectTwcGeRequestDetailsPage.constants';
import { IProjectTwcGeRequest, IUpsertProjectTwcGeRequestRequest } from '../projectTwcGeRequest.models';
import { EProjectTwcGeRequestApplicationType, EProjectTwcGeRequestStatus } from '../projectTwcGeRequest.constants';
import {
  useApproveProjectTwcGeRequestByEm,
  useApproveProjectTwcGeRequestByHoe,
  useApproveProjectTwcGeRequestByPm,
  useCancelProjectTwcGeRequest,
  useFetchProjectTwcGeRequestById,
  useIssueProjectTwcGeRequestToPm,
  useRevokeProjectTwcGeRequest,
  useSaveProjectTwcGeRequestAsDraft,
} from '../projectTwcGeRequest.queries';

export const useUpsertTwcGeRequest = (
  projectId: number,
  mode: DetailPageAccessMode,
  id?: number,
  applicationType?: EProjectTwcGeRequestApplicationType,
) => {
  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const appConfigs = React.useContext(AppConfigContext);
  const isProjectArchived = useProjectIsArchived();
  const [isUploadingCv, setIsUploadingCv] = React.useState(false);
  const isFormDirty = useFormMethods.formState.isDirty;
  const nominatedUser = useFormMethods.getValues(ProjectTwcGeDetailFormKeys.NOMINATED_STAFF);
  const isNominatedUser = React.useMemo(() => {
    if (nominatedUser && (nominatedUser as IProjectUserProfileOption[]).length > 0) {
      return Number(nominatedUser[0]?.id) === appConfigs.configs.myProjectUserProfile?.id;
    }
    return false;
  }, [nominatedUser]);

  const queryClient = useQueryClient();
  const hasInitRight =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ETwcTwcmApplicationPermission.INITATE_TWC]) ||
    isPermittedAsSystemUser(appConfigs.configs.myPermissions, [ETwcTwcmApplicationPermission.INITATE_TWC]);
  const hasIssueRight =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ETwcTwcmApplicationPermission.ISSUE_TWC]) ||
    isPermittedAsSystemUser(appConfigs.configs.myPermissions, [ETwcTwcmApplicationPermission.ISSUE_TWC]);
  const hasEmApproveRight = isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
    ETwcTwcmApplicationPermission.EM_APPROVE,
  ]);
  const hasHoeApproveRight = isPermittedAsSystemUser(appConfigs.configs.myPermissions, [
    ERolePermission.HEAD_OF_ENGINEERING,
  ]);
  const hasRevokeRight =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ETwcTwcmApplicationPermission.REVOKE]) ||
    isPermittedAsSystemUser(appConfigs.configs.myPermissions, [ETwcTwcmApplicationPermission.REVOKE]);
  const hasDownloadRight =
    isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ETwcTwcmApplicationPermission.DOWNLOAD_CV]) ||
    isPermittedAsSystemUser(appConfigs.configs.myPermissions, [ETwcTwcmApplicationPermission.DOWNLOAD_CV]) ||
    isNominatedUser;
  const currentProposedAs = useFormMethods.watch(ProjectTwcGeDetailFormKeys.PROPOSED_AS);

  const { navigate } = useNavigate();

  const {
    data: projectTwcGeRequest,
    isLoading: isLoadingProjectTwcGeRequest,
    isRefetching: isRefetchingProjectTwcGeRequest,
    error: errorGetProjectTwcGeRequest,
  } = useFetchProjectTwcGeRequestById({ projectId, id: id || 0 }, !isFormDirty && mode !== 'Create');

  const { data: projectUserProfileOptions } = useFetchProjectUserProfileOptionsWithSearch({
    shouldConvertOptions: false,
  })({ projectId, keyword: '', pageFilter: { skip: 0, take: DEFAULT_SELECT_PAGE_SIZE } });

  const isGeRequestAndProposedAsTwc = React.useMemo(() => {
    return (
      ((projectTwcGeRequest && projectTwcGeRequest.applicationType === EProjectTwcGeRequestApplicationType.GE) ||
        (!projectTwcGeRequest && applicationType === EProjectTwcGeRequestApplicationType.GE)) &&
      (!currentProposedAs || currentProposedAs.length <= 0 || currentProposedAs[0].data?.name === 'TWC')
    );
  }, [JSON.stringify(currentProposedAs), JSON.stringify(projectTwcGeRequest), applicationType]);

  const successHandle = (toastMsg: string, result?: IItemResponse<IProjectTwcGeRequest>) => {
    useFormMethods.reset();
    queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.PROJECT_TWC_GE_REQUEST_BY_ID, result?.item?.id || id]);
    toastMessage(
      <ToastMessage message={toastMsg} toastOptions={{ type: 'success', closeOnClick: true, autoClose: 3000 }} />,
    );

    if (mode === 'Create' && result?.item)
      navigate(routes.users.twcRequest.detail.path, { id: result.item.id.toString() });
  };

  const onClickHandleUpsert = React.useMemo(() => {
    return (mutate: UseMutateFunction<IItemResponse<IProjectTwcGeRequest>, AxiosErrorResponse, any, unknown>) => {
      useFormMethods.clearErrors();
      useFormMethods.handleSubmit((data: any) => {
        mutate({
          id,
          projectId,
          projectUserProfileId: Number(data[ProjectTwcGeDetailFormKeys.NOMINATED_STAFF]?.id),
          position: data[ProjectTwcGeDetailFormKeys.POSITION],
          grade: data[ProjectTwcGeDetailFormKeys.GRADE],
          proposedAs: Number(data[ProjectTwcGeDetailFormKeys.PROPOSED_AS][0]?.key),
          responsibility: data[ProjectTwcGeDetailFormKeys.RESPONSIBILITY],
          trainingEvidence: data[ProjectTwcGeDetailFormKeys.TRAINING_EVIDENCE],
          pmComments: data[ProjectTwcGeDetailFormKeys.PM_COMMENTS],
          emComments: data[ProjectTwcGeDetailFormKeys.EM_COMMENTS],
          hoeComments: data[ProjectTwcGeDetailFormKeys.HOE_COMMENTS],
          applicationType: projectTwcGeRequest?.applicationType || applicationType,
          rowVersion: projectTwcGeRequest?.rowVersion,
          tempCv: data[ProjectTwcGeDetailFormKeys.ATTACHED_CV],
        } as IUpsertProjectTwcGeRequestRequest);
      })();
    };
  }, [projectId, JSON.stringify(projectTwcGeRequest)]);

  const { error: errorSaveProjectTwcGeRequestAsDraft, loadingButtonProps: saveDraftButtonProps } =
    useMutationWithPageTopBarLoadingButton({
      title: 'Save Draft',
      componentType: ComponentType.Secondary,
      onClick: onClickHandleUpsert,
      disable: isUploadingCv,
      useMutation: useSaveProjectTwcGeRequestAsDraft,
      onSuccess: (variables, result) => {
        successHandle('Save request success', result);
      },
    });

  const { error: errorIssueProjectTwcGeRequestToPm, loadingButtonProps: issueToPmButtonProps } =
    useMutationWithPageTopBarLoadingButton({
      title: 'Issue to PM',
      componentType: ComponentType.Primary,
      onClick: onClickHandleUpsert,
      disable: isUploadingCv,
      useMutation: useIssueProjectTwcGeRequestToPm,
      onSuccess: (variables, result) => {
        successHandle('Issue request to PM success', result);
      },
    });

  const { error: errorApproveProjectTwcGeRequestByPm, loadingButtonProps: approveByPmButtonProps } =
    useMutationWithPageTopBarLoadingButton({
      title: 'Issue to EM',
      componentType: ComponentType.Primary,
      onClick: onClickHandleUpsert,
      disable: isUploadingCv,
      useMutation: useApproveProjectTwcGeRequestByPm,
      onSuccess: (variables, result) => {
        successHandle('Issue request to EM success', result);
      },
    });

  const { error: errorApproveProjectTwcGeRequestByEm, loadingButtonProps: approveByEmButtonProps } =
    useMutationWithPageTopBarLoadingButton({
      title: isGeRequestAndProposedAsTwc ? 'Issue to HoE' : 'Approve',
      componentType: ComponentType.Primary,
      onClick: onClickHandleUpsert,
      disable: isUploadingCv,
      useMutation: useApproveProjectTwcGeRequestByEm,
      onSuccess: (variables, result) => {
        successHandle(
          isGeRequestAndProposedAsTwc ? 'Issue request to HoE success' : 'Approve request by EM success',
          result,
        );
      },
    });

  const { error: errorApproveProjectTwcGeRequestByHoe, loadingButtonProps: approveByHoeButtonProps } =
    useMutationWithPageTopBarLoadingButton({
      title: 'Approve',
      componentType: ComponentType.Primary,
      onClick: onClickHandleUpsert,
      disable: isUploadingCv,
      useMutation: useApproveProjectTwcGeRequestByHoe,
      onSuccess: (variables, result) => {
        successHandle('Approve request by HoE success', result);
      },
    });

  const { error: errorCancelProjectTwcGeRequest, loadingButtonProps: cancelButtonProps } =
    useMutationWithPageTopBarLoadingButton({
      title: 'Cancel request',
      componentType: ComponentType.Danger,
      onClick: (mutate) => {
        if (!projectTwcGeRequest) return;
        mutate({
          projectId: projectTwcGeRequest.projectId,
          id: projectTwcGeRequest.id,
          applicationType: projectTwcGeRequest.applicationType,
        });
      },
      disable: isUploadingCv,
      useMutation: useCancelProjectTwcGeRequest,
      onSuccess: () => {
        successHandle('Cancel request success');
      },
      buttonIconName: 'FileExcel',
    });

  const { error: errorRevokeProjectTwcGeRequest, loadingButtonProps: revokeButtonProps } =
    useMutationWithPageTopBarLoadingButton({
      title: 'Revoke',
      componentType: ComponentType.Danger,
      onClick: (mutate) => {
        if (!projectTwcGeRequest) return;
        mutate({
          projectId: projectTwcGeRequest.projectId,
          id: projectTwcGeRequest.id,
          applicationType: projectTwcGeRequest.applicationType,
        });
      },
      disable: isUploadingCv,
      useMutation: useRevokeProjectTwcGeRequest,
      onSuccess: () => {
        successHandle('Revoke request success');
      },
    });

  const upsertButtonsProps = React.useMemo(() => {
    if (
      mode === 'View' ||
      projectTwcGeRequest?.status === EProjectTwcGeRequestStatus.CANCELLED ||
      projectTwcGeRequest?.status === EProjectTwcGeRequestStatus.REVOKE
    )
      return [];

    if (projectTwcGeRequest?.status === EProjectTwcGeRequestStatus.COMPLETED && hasRevokeRight)
      return [revokeButtonProps];

    // TODO for GE request, add button Issue to HOE / HOE approve / HOE Reject?

    if (hasInitRight && mode === 'Create' && (id || 0) <= 0) return [saveDraftButtonProps, issueToPmButtonProps];

    switch (projectTwcGeRequest?.status) {
      case EProjectTwcGeRequestStatus.DRAFT:
        return hasInitRight ? [saveDraftButtonProps, issueToPmButtonProps, cancelButtonProps] : [];
      case EProjectTwcGeRequestStatus.WAITING_FOR_PM:
        return hasIssueRight ? [saveDraftButtonProps, approveByPmButtonProps, cancelButtonProps] : [];
      case EProjectTwcGeRequestStatus.WAITING_FOR_EM:
        return hasEmApproveRight ? [saveDraftButtonProps, approveByEmButtonProps, cancelButtonProps] : [];
      case EProjectTwcGeRequestStatus.WAITING_FOR_HOE:
        return hasHoeApproveRight ? [saveDraftButtonProps, approveByHoeButtonProps, cancelButtonProps] : [];
      default:
        return [];
    }
  }, [
    mode,
    isUploadingCv,
    JSON.stringify(projectTwcGeRequest),
    hasInitRight,
    hasIssueRight,
    hasEmApproveRight,
    hasHoeApproveRight,
    hasRevokeRight,
    revokeButtonProps,
    saveDraftButtonProps,
    cancelButtonProps,
    issueToPmButtonProps,
    approveByPmButtonProps,
    approveByEmButtonProps,
    approveByHoeButtonProps,
  ]);

  const shouldReadOnly = React.useMemo(() => {
    if (isProjectArchived) return true;
    switch (projectTwcGeRequest?.status) {
      case undefined:
      case EProjectTwcGeRequestStatus.DRAFT:
        return !hasInitRight;
      case EProjectTwcGeRequestStatus.WAITING_FOR_PM:
        return !hasIssueRight;
      case EProjectTwcGeRequestStatus.WAITING_FOR_EM:
        return !hasEmApproveRight;
      case EProjectTwcGeRequestStatus.WAITING_FOR_HOE:
        return !hasHoeApproveRight;
      case EProjectTwcGeRequestStatus.COMPLETED:
      case EProjectTwcGeRequestStatus.REVOKE:
      case EProjectTwcGeRequestStatus.CANCELLED:
      default:
        return true;
    }
  }, [
    mode,
    JSON.stringify(projectTwcGeRequest),
    hasInitRight,
    hasIssueRight,
    hasEmApproveRight,
    hasHoeApproveRight,
    isProjectArchived,
  ]);

  const hasCvUploadRight = React.useMemo(() => {
    if (hasInitRight && mode === 'Create' && (id || 0) <= 0) return true;
    switch (projectTwcGeRequest?.status) {
      case EProjectTwcGeRequestStatus.DRAFT:
        return hasInitRight;
      case EProjectTwcGeRequestStatus.WAITING_FOR_PM:
        return hasIssueRight;
      case EProjectTwcGeRequestStatus.WAITING_FOR_EM:
        return hasEmApproveRight;
      default:
        return false;
    }
  }, [JSON.stringify(projectTwcGeRequest), hasInitRight, hasIssueRight, hasEmApproveRight, hasRevokeRight]);

  const hasCvDownloadRight = React.useMemo(() => {
    return (
      (hasInitRight && mode === 'Create' && (id || 0) <= 0) ||
      hasDownloadRight ||
      (projectTwcGeRequest &&
        ((projectTwcGeRequest.status === EProjectTwcGeRequestStatus.DRAFT &&
          (projectTwcGeRequest.requestedByProjectProfileId === appConfigs?.configs?.myProjectUserProfile?.id ||
            projectTwcGeRequest.projectUserProfileId === appConfigs?.configs?.myProjectUserProfile?.id)) ||
          (projectTwcGeRequest.status === EProjectTwcGeRequestStatus.WAITING_FOR_PM && hasIssueRight)))
    );
  }, [
    JSON.stringify(projectTwcGeRequest),
    JSON.stringify(appConfigs?.configs?.myProjectUserProfile),
    hasInitRight,
    hasIssueRight,
    hasEmApproveRight,
    hasRevokeRight,
  ]);

  const upsertErrors = React.useMemo(
    () => [
      { error: errorSaveProjectTwcGeRequestAsDraft, genericError: 'Save request as draft failed' },
      { error: errorIssueProjectTwcGeRequestToPm, genericError: 'Issue request to PM failed' },
      { error: errorApproveProjectTwcGeRequestByPm, genericError: 'Approve request by PM failed' },
      { error: errorApproveProjectTwcGeRequestByEm, genericError: 'Approve request by EM failed' },
      { error: errorApproveProjectTwcGeRequestByHoe, genericError: 'Approve request by HoE failed' },
      { error: errorCancelProjectTwcGeRequest, genericError: 'Cancel request failed' },
      { error: errorRevokeProjectTwcGeRequest, genericError: 'Cancel request failed' },
    ],
    [
      errorSaveProjectTwcGeRequestAsDraft,
      errorIssueProjectTwcGeRequestToPm,
      errorApproveProjectTwcGeRequestByPm,
      errorApproveProjectTwcGeRequestByEm,
      errorApproveProjectTwcGeRequestByHoe,
      errorCancelProjectTwcGeRequest,
      errorRevokeProjectTwcGeRequest,
    ],
  );

  return {
    useFormMethods,
    isFormDirty,
    projectTwcGeRequest,
    errorGetProjectTwcGeRequest,
    isLoadingProjectTwcGeRequest,
    isRefetchingProjectTwcGeRequest,
    upsertButtonsProps,
    upsertErrors,
    hasInitRight,
    hasIssueRight,
    hasEmApproveRight,
    hasHoeApproveRight,
    hasRevokeRight,
    hasCvDownloadRight,
    hasCvUploadRight,
    setIsUploadingCv,
    isGeRequestAndProposedAsTwc,
    shouldReadOnly,
    projectUserProfileOptions,
  };
};
